import Cookies from 'js-cookie'
import type { BillingState } from '@/requests/billing'
import { PLAN_MAX_SHOPS, PLAN_MAX_ITEMS } from '@/lib/plan-limits'

export type CachedPlanKey = 'FREE' | 'PLUS' | 'PRO' | 'ENTERPRISE'

const PLAN_CACHE_COOKIE = 'mp_plan_cache'
const CACHE_MAX_AGE_DAYS = 30

type PlanCacheEntry = {
  plan: CachedPlanKey
  updatedAt: number
}

type PlanCacheStore = Record<string, PlanCacheEntry>

function resolvePlanKey(plan: string | undefined): CachedPlanKey {
  if (plan === 'PLUS' || plan === 'PRO' || plan === 'ENTERPRISE') return plan
  return 'FREE'
}

function readStore(): PlanCacheStore {
  const raw = Cookies.get(PLAN_CACHE_COOKIE)
  if (!raw) return {}

  try {
    const parsed = JSON.parse(raw) as PlanCacheStore
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeStore(store: PlanCacheStore): void {
  Cookies.set(PLAN_CACHE_COOKIE, JSON.stringify(store), {
    expires: CACHE_MAX_AGE_DAYS,
    path: '/',
    sameSite: 'Lax',
  })
}

export function getCachedPlan(shopId: string): CachedPlanKey | null {
  if (!shopId) return null
  const entry = readStore()[shopId]
  return entry?.plan ?? null
}

export function setCachedPlan(shopId: string, plan: string): void {
  if (!shopId) return

  const store = readStore()
  store[shopId] = {
    plan: resolvePlanKey(plan),
    updatedAt: Date.now(),
  }
  writeStore(store)
}

export function clearPlanCache(shopId?: string): void {
  if (!shopId) {
    Cookies.remove(PLAN_CACHE_COOKIE, { path: '/' })
    return
  }

  const store = readStore()
  if (!(shopId in store)) return

  delete store[shopId]
  if (Object.keys(store).length === 0) {
    Cookies.remove(PLAN_CACHE_COOKIE, { path: '/' })
    return
  }

  writeStore(store)
}

export function getCachedBillingPlaceholder(
  shopId: string,
): BillingState | undefined {
  const plan = getCachedPlan(shopId)
  if (!plan) return undefined

  const maxShops = PLAN_MAX_SHOPS[plan] ?? 1
  const maxItems = PLAN_MAX_ITEMS[plan] ?? PLAN_MAX_ITEMS.FREE

  return {
    subscriptions: [],
    entitlements: { plan, plugins: [] },
    total_monthly_cents: 0,
    currency: 'USD',
    shop_quota: {
      used: 0,
      max_shops: maxShops,
      can_create: true,
    },
    item_quota: {
      used: 0,
      max_items: maxItems,
      remaining: maxItems === null ? null : maxItems,
      can_create: true,
    },
  }
}
